import { useState, useEffect, useMemo } from 'react'
import Pagination from '../shared/Pagination'
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts'

// Shared signed-in client + paged reader (see lib/supabase.js). The push
// tables' RLS policy is authenticated-only, and both are well past PostgREST's
// 1000-row cap (12.5k entries / 9.8k events on 2026-09-07): an un-paged select
// returned the 1000 newest entries next to the 1000 *oldest* events, so
// Delivered read 0 and Pending read 1000.
import { fetchAllRows as fetchAll } from '../../lib/supabase'

export default function DashboardTab() {
  const [stats, setStats] = useState({
    totalSent: 0,
    delivered: 0,
    clicked: 0,
    closed: 0,
    failed: 0,
    pending: 0,
    uniqueDevices: 0,
    uniqueTags: 0
  })
  const [recentActivity, setRecentActivity] = useState([])
  const [allEntries, setAllEntries] = useState([])
  const [allEvents, setAllEvents] = useState([])
  const [tagMap, setTagMap] = useState(new Map())
  const [loading, setLoading] = useState(true)

  // Analytics accordion state
  const [analyticsOpen, setAnalyticsOpen] = useState(false)
  const [engagementOpen, setEngagementOpen] = useState(false)
  const [statusDistOpen, setStatusDistOpen] = useState(false)
  const [interactionOpen, setInteractionOpen] = useState(false)
  const [deliveryTimeOpen, setDeliveryTimeOpen] = useState(false)
  const [activityDate, setActivityDate] = useState(new Date().toISOString().split('T')[0])

  // Filter states
  const [statusFilter, setStatusFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [dateRange, setDateRange] = useState('all') // all, today, week, month
  const [tagFilter, setTagFilter] = useState('all')

  // Activity-table pagination — client-side because the table is fed by
  // recentActivity which is already a derivative of filteredEntries.
  // Stats and charts above continue to use the full filtered set.
  const [activityPage, setActivityPage] = useState(1)
  const [activityPageSize, setActivityPageSize] = useState(25)

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      setLoading(true)

      // Get every push notification sent (paged past the 1000-row cap)
      const entries = await fetchAll(
        'PushEntries',
        'PushEntryId, Title, Body, ClickUrl, Created, TagId',
        { column: 'Created', ascending: false }
      )

      setAllEntries(entries)
      const totalSent = entries.length

      // Get every delivery event, newest first so a partial load still
      // favours the entries shown at the top of the activity list.
      const events = await fetchAll(
        'PushEntryDeliveryEvents',
        'PushEntryDeliveryEventId, Status, Timestamp, PushEntryId, DeviceToken',
        { column: 'Timestamp', ascending: false }
      )

      setAllEvents(events)

      // Get tags for recent activity display
      const tags = await fetchAll('PushTags', 'PushTagId, Tag')

      const newTagMap = new Map(tags?.map(t => [t.PushTagId, t.Tag]) || [])
      setTagMap(newTagMap)

      // Count events by type
      const eventCounts = {
        delivered: 0,
        clicked: 0,
        closed: 0,
        failed: 0
      }

      // Track unique devices and entries with delivery
      const uniqueDevices = new Set()
      const deliveredEntries = new Set()

      if (events) {
        events.forEach(event => {
          const status = String(event.Status || '').toLowerCase()

          if (status === 'delivered') {
            eventCounts.delivered++
            deliveredEntries.add(event.PushEntryId)
          }
          else if (status === 'clicked') eventCounts.clicked++
          else if (status === 'closed') eventCounts.closed++
          else if (status === 'failed' || status === 'error') eventCounts.failed++

          if (event.DeviceToken) {
            uniqueDevices.add(event.DeviceToken)
          }
        })
      }

      // Calculate pending (sent but no delivery event)
      const pending = totalSent - deliveredEntries.size - eventCounts.failed

      // Count unique tags
      const uniqueTags = new Set(entries?.map(e => e.TagId).filter(Boolean))

      setStats({
        totalSent,
        ...eventCounts,
        pending: Math.max(0, pending),
        uniqueDevices: uniqueDevices.size,
        uniqueTags: uniqueTags.size
      })

      // Process recent activity
      processRecentActivity(entries, events, newTagMap)

    } catch (error) {
      console.error('Failed to load dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const processRecentActivity = (entries, events, tagMapData) => {
    const recent = entries?.map(entry => {
      const entryEvents = events?.filter(e => e.PushEntryId === entry.PushEntryId)
      const latestEvent = entryEvents?.sort((a, b) =>
        new Date(b.Timestamp) - new Date(a.Timestamp)
      )[0]

      return {
        ...entry,
        tag: tagMapData.get(entry.TagId) || 'N/A',
        latestStatus: latestEvent?.Status || 'sent',
        latestTimestamp: latestEvent?.Timestamp || entry.Created,
        deliveryCount: entryEvents?.filter(e => e.Status === 'delivered').length || 0
      }
    }) || []

    setRecentActivity(recent)
  }

  // Helper to get date range start date
  const getDateRangeStart = (range) => {
    if (range === 'all') return null
    const now = new Date()
    switch (range) {
      case 'today':
        return new Date(now.getFullYear(), now.getMonth(), now.getDate())
      case 'week':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      case 'month':
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      default:
        return null
    }
  }

  // Filter entries based on all filters
  // Tags actually present on at least one entry — drives the dropdown
  // options. Same approach as EntriesTab: keeps the menu relevant to what
  // the operator can actually filter to.
  const tagsInUse = useMemo(() => {
    const seen = new Map()
    for (const entry of allEntries) {
      if (entry.TagId == null) continue
      const label = tagMap.get(entry.TagId)
      if (!label || seen.has(entry.TagId)) continue
      seen.set(entry.TagId, label)
    }
    return Array.from(seen.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [allEntries, tagMap])

  const filteredEntries = useMemo(() => {
    let filtered = [...allEntries]
    const startDate = getDateRangeStart(dateRange)

    // Date range filter
    if (startDate) {
      filtered = filtered.filter(e => new Date(e.Created) >= startDate)
    }

    // Tag filter — exact match on TagId so phone numbers and audience
    // tags don't collide on substring overlap.
    if (tagFilter !== 'all') {
      filtered = filtered.filter(e => String(e.TagId) === String(tagFilter))
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(e =>
        e.Title?.toLowerCase().includes(query) ||
        e.Body?.toLowerCase().includes(query) ||
        tagMap.get(e.TagId)?.toLowerCase().includes(query)
      )
    }

    return filtered
  }, [allEntries, dateRange, searchQuery, tagMap, tagFilter])

  // Get entry IDs that match filters for event filtering
  const filteredEntryIds = useMemo(() => {
    return new Set(filteredEntries.map(e => e.PushEntryId))
  }, [filteredEntries])

  // Filter events based on filtered entries and status filter
  const filteredEvents = useMemo(() => {
    let filtered = allEvents.filter(e => filteredEntryIds.has(e.PushEntryId))

    // Status filter - filter events by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(e =>
        e.Status?.toLowerCase() === statusFilter.toLowerCase()
      )
    }

    return filtered
  }, [allEvents, filteredEntryIds, statusFilter])

  // Calculate filtered stats
  const filteredStats = useMemo(() => {
    const eventCounts = {
      delivered: 0,
      clicked: 0,
      closed: 0,
      failed: 0
    }

    const uniqueDevices = new Set()
    const deliveredEntries = new Set()

    // Use all events for filtered entries (not status-filtered) for accurate stats
    const eventsForStats = allEvents.filter(e => filteredEntryIds.has(e.PushEntryId))

    eventsForStats.forEach(event => {
      const status = String(event.Status || '').toLowerCase()

      if (status === 'delivered') {
        eventCounts.delivered++
        deliveredEntries.add(event.PushEntryId)
      }
      else if (status === 'clicked') eventCounts.clicked++
      else if (status === 'closed') eventCounts.closed++
      else if (status === 'failed' || status === 'error') eventCounts.failed++

      if (event.DeviceToken) {
        uniqueDevices.add(event.DeviceToken)
      }
    })

    const totalSent = filteredEntries.length
    const pending = totalSent - deliveredEntries.size - eventCounts.failed
    const uniqueTags = new Set(filteredEntries.map(e => e.TagId).filter(Boolean))

    return {
      totalSent,
      ...eventCounts,
      pending: Math.max(0, pending),
      uniqueDevices: uniqueDevices.size,
      uniqueTags: uniqueTags.size
    }
  }, [filteredEntries, filteredEntryIds, allEvents])

  // Filter recent activity based on filters. Limit removed — pagination
  // below handles slicing so the user can browse the full list instead
  // of being silently cut off at 50.
  const filteredActivity = useMemo(() => {
    let filtered = recentActivity.filter(a => filteredEntryIds.has(a.PushEntryId))

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(a =>
        a.latestStatus.toLowerCase() === statusFilter.toLowerCase()
      )
    }

    return filtered
  }, [recentActivity, filteredEntryIds, statusFilter])

  // Slice the activity list to the current page.
  const pagedActivity = useMemo(() => {
    const from = (activityPage - 1) * activityPageSize
    return filteredActivity.slice(from, from + activityPageSize)
  }, [filteredActivity, activityPage, activityPageSize])

  // Reset activity page when any filter or page size changes — staying
  // on page 5 after a filter shrinks the result set is a footgun.
  useEffect(() => {
    setActivityPage(1)
  }, [statusFilter, searchQuery, dateRange, tagFilter, activityPageSize])

  // Check if any filters are active
  const hasActiveFilters = statusFilter !== 'all' || searchQuery.trim() || dateRange !== 'all' || tagFilter !== 'all'

  // Generate time-series data for charts based on filtered data
  const chartData = useMemo(() => {
    if (!filteredEntries.length) return { daily: [], statusDistribution: [] }

    // Determine date range for chart
    const chartDays = dateRange === 'week' ? 7 : dateRange === 'today' ? 1 : 30

    // Group entries by date
    const dailyData = {}
    const dateList = []

    for (let i = chartDays - 1; i >= 0; i--) {
      const date = new Date()
      date.setDate(date.getDate() - i)
      const dateStr = date.toISOString().split('T')[0]
      dateList.push(dateStr)
      dailyData[dateStr] = { date: dateStr, sent: 0, delivered: 0, clicked: 0, dismissed: 0 }
    }

    // Count filtered entries per day
    filteredEntries.forEach(entry => {
      const dateStr = new Date(entry.Created).toISOString().split('T')[0]
      if (dailyData[dateStr]) {
        dailyData[dateStr].sent++
      }
    })

    // Count events per day (using events from filtered entries only)
    const eventsForChart = allEvents.filter(e => filteredEntryIds.has(e.PushEntryId))
    eventsForChart.forEach(event => {
      const dateStr = new Date(event.Timestamp).toISOString().split('T')[0]
      if (dailyData[dateStr]) {
        const status = String(event.Status || '').toLowerCase()
        if (status === 'delivered') dailyData[dateStr].delivered++
        else if (status === 'clicked') dailyData[dateStr].clicked++
        else if (status === 'closed') dailyData[dateStr].dismissed++
      }
    })

    // Calculate rates
    const daily = dateList.map(dateStr => {
      const d = dailyData[dateStr]
      return {
        ...d,
        displayDate: new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        deliveryRate: d.sent > 0 ? Math.round((d.delivered / d.sent) * 100) : 0,
        clickRate: d.delivered > 0 ? Math.round((d.clicked / d.delivered) * 100) : 0
      }
    })

    // Status distribution for pie chart (using filtered stats)
    const statusDistribution = [
      { name: 'Delivered', value: filteredStats.delivered, color: '#22c55e' },
      { name: 'Clicked', value: filteredStats.clicked, color: '#6366f1' },
      { name: 'Dismissed', value: filteredStats.closed, color: '#6b7280' },
      { name: 'Failed', value: filteredStats.failed, color: '#ef4444' },
      { name: 'Pending', value: filteredStats.pending, color: '#eab308' }
    ].filter(s => s.value > 0)

    return { daily, statusDistribution }
  }, [filteredEntries, filteredEntryIds, allEvents, filteredStats, dateRange])

  // Hourly activity breakdown for a selected date
  const hourlyActivity = useMemo(() => {
    const hours = Array.from({ length: 24 }, (_, i) => ({
      hour: i,
      label: `${String(i).padStart(2, '0')}:00`,
      delivered: 0,
      clicked: 0,
      dismissed: 0,
      failed: 0
    }))

    const eventsForDate = allEvents.filter(e => {
      if (!filteredEntryIds.has(e.PushEntryId)) return false
      const eventDate = new Date(e.Timestamp).toISOString().split('T')[0]
      return eventDate === activityDate
    })

    eventsForDate.forEach(event => {
      const h = new Date(event.Timestamp).getHours()
      const status = String(event.Status || '').toLowerCase()
      if (status === 'delivered') hours[h].delivered++
      else if (status === 'clicked') hours[h].clicked++
      else if (status === 'closed') hours[h].dismissed++
      else if (status === 'failed' || status === 'error') hours[h].failed++
    })

    return hours
  }, [allEvents, filteredEntryIds, activityDate])

  const getStatColor = (type) => {
    switch(type) {
      case 'totalSent': return 'bg-blue-500'
      case 'delivered': return 'bg-green-500'
      case 'clicked': return 'bg-indigo-500'
      case 'closed': return 'bg-gray-500'
      case 'failed': return 'bg-red-500'
      case 'pending': return 'bg-yellow-500'
      case 'uniqueDevices': return 'bg-purple-500'
      case 'uniqueTags': return 'bg-teal-500'
      default: return 'bg-gray-500'
    }
  }

  const getStatIcon = (type) => {
    switch(type) {
      case 'totalSent':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        )
      case 'delivered':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      case 'clicked':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
          </svg>
        )
      case 'closed':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        )
      case 'failed':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        )
      case 'pending':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      case 'uniqueDevices':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        )
      case 'uniqueTags':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
        )
      default:
        return null
    }
  }

  const getPercentage = (value, total) => {
    if (total === 0) return 0
    return Math.round((value / total) * 100)
  }

  const getTooltip = (type) => {
    switch(type) {
      case 'totalSent':
        return 'Total number of push notifications sent through the system'
      case 'delivered':
        return 'Push notifications successfully delivered to devices. Calculated as (delivered / total sent) x 100'
      case 'clicked':
        return 'Number of push notifications clicked by users. Calculated as (clicked / delivered) x 100'
      case 'closed':
        return 'Push notifications dismissed by users without clicking'
      case 'failed':
        return 'Push notifications that failed to deliver (invalid tokens, etc.)'
      case 'pending':
        return 'Push notifications sent but not yet confirmed as delivered or failed'
      case 'uniqueDevices':
        return 'Number of unique devices that have received push notifications'
      case 'uniqueTags':
        return 'Number of unique tags/audiences targeted with push notifications'
      default:
        return ''
    }
  }

  const getStatusBadge = (status) => {
    const statusLower = String(status || 'sent').toLowerCase()

    if (statusLower === 'delivered') {
      return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full">Delivered</span>
    } else if (statusLower === 'clicked') {
      return <span className="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200 rounded-full">Clicked</span>
    } else if (statusLower === 'closed') {
      return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 rounded-full">Dismissed</span>
    } else if (statusLower === 'failed' || statusLower === 'error') {
      return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full">Failed</span>
    } else if (statusLower === 'sent') {
      return <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 rounded-full">Sent</span>
    }

    return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200 rounded-full capitalize">{status}</span>
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Push Notification Dashboard</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Overview of push notification delivery performance</p>
        </div>
        <button
          onClick={loadDashboardData}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span>Refresh</span>
        </button>
      </div>

      {/* Filter indicator */}
      {hasActiveFilters && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <span className="text-sm text-blue-700 dark:text-blue-300">
              <strong>Filters active:</strong>
              {dateRange !== 'all' && ` ${dateRange === 'today' ? 'Today' : dateRange === 'week' ? 'Last 7 days' : 'Last 30 days'}`}
              {statusFilter !== 'all' && ` • Status: ${statusFilter}`}
              {tagFilter !== 'all' && ` • Tag: ${tagMap.get(Number(tagFilter)) || tagFilter}`}
              {searchQuery && ` • Search: "${searchQuery}"`}
            </span>
          </div>
          <button
            onClick={() => {
              setStatusFilter('all')
              setSearchQuery('')
              setDateRange('all')
              setTagFilter('all')
            }}
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 font-medium"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Sent */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-help transition hover:shadow-lg"
          title={getTooltip('totalSent')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Sent</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{filteredStats.totalSent}</p>
            </div>
            <div className={`${getStatColor('totalSent')} p-3 rounded-lg text-white`}>
              {getStatIcon('totalSent')}
            </div>
          </div>
        </div>

        {/* Delivered */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-help transition hover:shadow-lg"
          title={getTooltip('delivered')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Delivered</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{filteredStats.delivered}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {getPercentage(filteredStats.delivered, filteredStats.totalSent)}% delivery rate
              </p>
            </div>
            <div className={`${getStatColor('delivered')} p-3 rounded-lg text-white`}>
              {getStatIcon('delivered')}
            </div>
          </div>
        </div>

        {/* Clicked */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-help transition hover:shadow-lg"
          title={getTooltip('clicked')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Clicked</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{filteredStats.clicked}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {getPercentage(filteredStats.clicked, filteredStats.delivered)}% click rate
              </p>
            </div>
            <div className={`${getStatColor('clicked')} p-3 rounded-lg text-white`}>
              {getStatIcon('clicked')}
            </div>
          </div>
        </div>

        {/* Dismissed */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-help transition hover:shadow-lg"
          title={getTooltip('closed')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Dismissed</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{filteredStats.closed}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {getPercentage(filteredStats.closed, filteredStats.delivered)}% dismiss rate
              </p>
            </div>
            <div className={`${getStatColor('closed')} p-3 rounded-lg text-white`}>
              {getStatIcon('closed')}
            </div>
          </div>
        </div>

        {/* Failed */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-help transition hover:shadow-lg"
          title={getTooltip('failed')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Failed</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{filteredStats.failed}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {getPercentage(filteredStats.failed, filteredStats.totalSent)}% failure rate
              </p>
            </div>
            <div className={`${getStatColor('failed')} p-3 rounded-lg text-white`}>
              {getStatIcon('failed')}
            </div>
          </div>
        </div>

        {/* Pending */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-help transition hover:shadow-lg"
          title={getTooltip('pending')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{filteredStats.pending}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Awaiting delivery confirmation
              </p>
            </div>
            <div className={`${getStatColor('pending')} p-3 rounded-lg text-white`}>
              {getStatIcon('pending')}
            </div>
          </div>
        </div>

        {/* Unique Devices */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-help transition hover:shadow-lg"
          title={getTooltip('uniqueDevices')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Unique Devices</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{filteredStats.uniqueDevices}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Devices reached
              </p>
            </div>
            <div className={`${getStatColor('uniqueDevices')} p-3 rounded-lg text-white`}>
              {getStatIcon('uniqueDevices')}
            </div>
          </div>
        </div>

        {/* Unique Tags */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-help transition hover:shadow-lg"
          title={getTooltip('uniqueTags')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Unique Tags</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{filteredStats.uniqueTags}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Audiences targeted
              </p>
            </div>
            <div className={`${getStatColor('uniqueTags')} p-3 rounded-lg text-white`}>
              {getStatIcon('uniqueTags')}
            </div>
          </div>
        </div>
      </div>

      {/* Analytics Accordion */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setAnalyticsOpen(!analyticsOpen)}
          className="w-full px-6 py-4 flex items-center justify-between text-left border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
        >
          <div className="flex items-center space-x-3">
            <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Performance Analytics</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">30-day trends and distribution insights</p>
            </div>
          </div>
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform ${analyticsOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {analyticsOpen && (
          <div className="p-6 space-y-4">
            {/* Daily Volume Chart - Always visible in analytics */}
            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-2">Daily Notification Volume</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Track your sending patterns and engagement over the last 30 days</p>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData.daily}>
                    <defs>
                      <linearGradient id="colorSent" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorDelivered" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                    <XAxis
                      dataKey="displayDate"
                      tick={{ fill: '#9ca3af', fontSize: 12 }}
                      tickLine={{ stroke: '#374151' }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fill: '#9ca3af', fontSize: 12 }}
                      tickLine={{ stroke: '#374151' }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1f2937',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                    />
                    <Legend />
                    <Area type="monotone" dataKey="sent" name="Sent" stroke="#3b82f6" fillOpacity={1} fill="url(#colorSent)" />
                    <Area type="monotone" dataKey="delivered" name="Delivered" stroke="#22c55e" fillOpacity={1} fill="url(#colorDelivered)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Engagement Rate Sub-Accordion */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button
                onClick={() => setEngagementOpen(!engagementOpen)}
                className="w-full px-4 py-3 flex items-center justify-between text-left bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-1.5 bg-green-100 dark:bg-green-900 rounded">
                    <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">Engagement Rate</span>
                </div>
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform ${engagementOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {engagementOpen && (
                <div className="p-4 bg-white dark:bg-gray-800">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Delivery and click-through rate trends</p>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData.daily}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                        <XAxis
                          dataKey="displayDate"
                          tick={{ fill: '#9ca3af', fontSize: 11 }}
                          interval="preserveStartEnd"
                        />
                        <YAxis
                          tick={{ fill: '#9ca3af', fontSize: 11 }}
                          domain={[0, 100]}
                          tickFormatter={(value) => `${value}%`}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1f2937',
                            border: '1px solid #374151',
                            borderRadius: '8px',
                            color: '#fff'
                          }}
                          formatter={(value) => [`${value}%`, '']}
                        />
                        <Legend />
                        <Line type="monotone" dataKey="deliveryRate" name="Delivery Rate" stroke="#22c55e" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="clickRate" name="Click Rate" stroke="#6366f1" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

            {/* Status Distribution Sub-Accordion */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button
                onClick={() => setStatusDistOpen(!statusDistOpen)}
                className="w-full px-4 py-3 flex items-center justify-between text-left bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-1.5 bg-indigo-100 dark:bg-indigo-900 rounded">
                    <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                    </svg>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">Status Distribution</span>
                </div>
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform ${statusDistOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {statusDistOpen && (
                <div className="p-4 bg-white dark:bg-gray-800">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Overall breakdown of notification outcomes</p>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={chartData.statusDistribution}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          labelLine={{ stroke: '#9ca3af' }}
                        >
                          {chartData.statusDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1f2937',
                            border: '1px solid #374151',
                            borderRadius: '8px',
                            color: '#fff'
                          }}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

            {/* User Interaction Breakdown Sub-Accordion */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button
                onClick={() => setInteractionOpen(!interactionOpen)}
                className="w-full px-4 py-3 flex items-center justify-between text-left bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-1.5 bg-purple-100 dark:bg-purple-900 rounded">
                    <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                    </svg>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">User Interaction Breakdown</span>
                </div>
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform ${interactionOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {interactionOpen && (
                <div className="p-4 bg-white dark:bg-gray-800">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Compare clicks vs dismissals to understand user engagement</p>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData.daily}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                        <XAxis
                          dataKey="displayDate"
                          tick={{ fill: '#9ca3af', fontSize: 11 }}
                          interval="preserveStartEnd"
                        />
                        <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1f2937',
                            border: '1px solid #374151',
                            borderRadius: '8px',
                            color: '#fff'
                          }}
                        />
                        <Legend />
                        <Bar dataKey="clicked" name="Clicked" fill="#6366f1" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="dismissed" name="Dismissed" fill="#6b7280" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

            {/* Hourly Activity Sub-Accordion */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button
                onClick={() => setDeliveryTimeOpen(!deliveryTimeOpen)}
                className="w-full px-4 py-3 flex items-center justify-between text-left bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-1.5 bg-amber-100 dark:bg-amber-900 rounded">
                    <svg className="w-4 h-4 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">Hourly Activity</span>
                </div>
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform ${deliveryTimeOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {deliveryTimeOpen && (
                <div className="p-4 bg-white dark:bg-gray-800">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Delivery events by hour of day — find peak activity windows</p>
                    <input
                      type="date"
                      value={activityDate}
                      onChange={(e) => setActivityDate(e.target.value)}
                      className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={hourlyActivity}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                        <XAxis
                          dataKey="label"
                          tick={{ fill: '#9ca3af', fontSize: 11 }}
                          interval={1}
                        />
                        <YAxis
                          tick={{ fill: '#9ca3af', fontSize: 11 }}
                          allowDecimals={false}
                          label={{ value: 'Events', angle: -90, position: 'insideLeft', fill: '#9ca3af', fontSize: 12 }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1f2937',
                            border: '1px solid #374151',
                            borderRadius: '8px',
                            color: '#fff'
                          }}
                        />
                        <Legend />
                        <Bar dataKey="delivered" name="Delivered" stackId="a" fill="#22c55e" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="clicked" name="Clicked" stackId="a" fill="#6366f1" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="dismissed" name="Dismissed" stackId="a" fill="#6b7280" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="failed" name="Failed" stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Recent Activity with Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {filteredActivity.length} notification{filteredActivity.length !== 1 ? 's' : ''}
                {statusFilter !== 'all' || searchQuery || dateRange !== 'all' ? ' (filtered)' : ''}
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1 max-w-xs">
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search title, body, or tag..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              title="Filter by latest event status on the entry"
              className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              <option value="delivered">Delivered</option>
              <option value="clicked">Clicked</option>
              <option value="closed">Dismissed</option>
              <option value="failed">Failed</option>
              <option value="sent">Sent (Pending)</option>
            </select>

            {/* Tag Filter */}
            <select
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              title="Filter by audience tag (only tags present on loaded entries are listed)"
              className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent max-w-[14rem]"
            >
              <option value="all">All Tags</option>
              {tagsInUse.map(({ id, label }) => (
                <option key={id} value={id}>{label}</option>
              ))}
            </select>

            {/* Date Range Filter */}
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              title="Filter by entry creation time"
              className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={() => {
                  setStatusFilter('all')
                  setSearchQuery('')
                  setDateRange('all')
                  setTagFilter('all')
                }}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Tag / Audience
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Delivered To
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Last Updated
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {pagedActivity.length > 0 ? (
                pagedActivity.map((activity) => (
                  <tr key={activity.PushEntryId} className="hover:bg-gray-50 dark:hover:bg-gray-900 transition">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{activity.Title}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">{activity.Body}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <span className="px-2 py-1 bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200 rounded text-xs">
                        {activity.tag}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(activity.latestStatus)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {activity.deliveryCount} device{activity.deliveryCount !== 1 ? 's' : ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(activity.latestTimestamp).toLocaleString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    {recentActivity.length === 0 ? 'No recent activity' : 'No results match your filters'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {filteredActivity.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700">
            <Pagination
              page={activityPage}
              pageSize={activityPageSize}
              total={filteredActivity.length}
              onPageChange={setActivityPage}
              onPageSizeChange={setActivityPageSize}
            />
          </div>
        )}
      </div>
    </div>
  )
}
